import { useState, useEffect } from 'react';
import { paymentApi } from '../api/payment.api';
import { useSocket } from '@/contexts/SocketContext';

/**
 * Hook to track unpaid payment count for navigation badge
 * Count includes: Pending + Unmatched payments (excluding Ignored)
 *
 * - Pending: Payments with affaires but NOT all "PAYE BANQUE"
 * - Unmatched: Payments with no affaire assigned
 * - Ignored: Excluded from count
 *
 * Updates in real-time via WebSocket events:
 * - payment:created (new payment synced)
 * - payment:allocated (payment assigned to affaires)
 * - payment:validated (payment status changed)
 * - payment:accepted (over/underpayment accepted)
 */
export const useUnpaidPaymentCount = () => {
  const [unpaidCount, setUnpaidCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { socket, isConnected } = useSocket();

  /**
   * Fetch unpaid payment count from API
   */
  const fetchUnpaidCount = async () => {
    try {
      setIsLoading(true);
      const response = await paymentApi.getUnpaidCount();
      if (response.success) {
        setUnpaidCount(response.data.unpaid_count);
        console.log('[useUnpaidPaymentCount] Count updated:', response.data.unpaid_count);
      }
    } catch (error) {
      console.error('[useUnpaidPaymentCount] Error fetching unpaid count:', error);
      // Don't show error to user, just log it
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch + refetch whenever the socket (re)connects.
  // Socket.IO does not buffer room events for a momentarily disconnected
  // client, so a payment:* event fired during a reconnect window is lost.
  // Re-fetching on every (re)connect heals that gap.
  useEffect(() => {
    fetchUnpaidCount();
  }, [isConnected]);

  // Safety-net poll so the badge always converges even when a socket event
  // was missed (mirrors the fallback the tracker/SMS badges already have).
  // Without this the count only listens to role-room events with no fallback,
  // which left it stale until a full page reload.
  useEffect(() => {
    const interval = setInterval(fetchUnpaidCount, 60000);
    return () => clearInterval(interval);
  }, []);

  // Listen for WebSocket events that should update the count
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handlePaymentCreated = () => {
      console.log('[useUnpaidPaymentCount] Received payment:created event');
      fetchUnpaidCount();
    };

    const handlePaymentAllocated = () => {
      console.log('[useUnpaidPaymentCount] Received payment:allocated event');
      fetchUnpaidCount();
    };

    const handlePaymentValidated = () => {
      console.log('[useUnpaidPaymentCount] Received payment:validated event');
      fetchUnpaidCount();
    };

    const handlePaymentAccepted = () => {
      console.log('[useUnpaidPaymentCount] Received payment:accepted event');
      fetchUnpaidCount();
    };

    // Register event listeners
    socket.on('payment:created', handlePaymentCreated);
    socket.on('payment:allocated', handlePaymentAllocated);
    socket.on('payment:validated', handlePaymentValidated);
    socket.on('payment:accepted', handlePaymentAccepted);

    console.log('[useUnpaidPaymentCount] WebSocket listeners attached');

    // Cleanup
    return () => {
      socket.off('payment:created', handlePaymentCreated);
      socket.off('payment:allocated', handlePaymentAllocated);
      socket.off('payment:validated', handlePaymentValidated);
      socket.off('payment:accepted', handlePaymentAccepted);
      console.log('[useUnpaidPaymentCount] WebSocket listeners removed');
    };
  }, [socket, isConnected]);

  return {
    unpaidCount,
    isLoading,
    refetch: fetchUnpaidCount
  };
};
